import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import A11yFontIcon from 'components/A11yFontIcon';
import TextButton from 'components/TextButton';
import './AutoPlayVideo.css';

const PLAY_TIMEOUT = 5000;
const PROGRESS_INTERVAL_RATE_MS = 1000;
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4; // From MediaError spec

class AutoPlayingVideo extends PureComponent {
  constructor(props) {
    super(props);

    this.videoEl = React.createRef();
    this.autoPlayTriggered = false;
    this.progressInterval = null;
    this.watchedOrSkippedTriggered = false;

    this.handlePageHide = this.handlePageHide.bind(this);
    this.handleClickPoster = this.handleClickPoster.bind(this);
    this.checkProgress = this.checkProgress.bind(this);
    this.triggerAutoPlay = this.triggerAutoPlay.bind(this);
    this.handlePlay = this.handlePlayChanged.bind(this, true);
    this.handlePause = this.handlePlayChanged.bind(this, false);
    this.handleEnded = this.handlePlayChanged.bind(this, false);
    this.handleVolumeChange = this.handleVolumeChange.bind(this);
    this.handleUnmuteButton = this.handleUnmuteButton.bind(this);
    this.handleError = this.handleError.bind(this);

    this.storedDefaultMuted = props.defaultAutoPlay && props.defaultMuted;
    this.state = {
      load: props.defaultAutoPlay,
      restartable: false,
      playing: false,
      muted: this.storedDefaultMuted,
      showFallback: false
    };
  }

  componentDidMount() {
    this.progressInterval = setInterval(
      this.checkProgress,
      PROGRESS_INTERVAL_RATE_MS
    );

    if (this.props.onSkipped) {
      window.addEventListener('pagehide', this.handlePageHide);
    }
  }

  componentWillUnmount() {
    clearInterval(this.progressInterval);
    this.checkProgress();
    if (!this.watchedOrSkippedTriggered) {
      this.watchedOrSkippedTriggered = true;
      this.props.onSkipped && this.props.onSkipped();
    }

    window.removeEventListener('pagehide', this.handlePageHide);
  }

  handlePageHide() {
    if (!this.watchedOrSkippedTriggered) {
      this.watchedOrSkippedTriggered = true;
      this.props.onSkipped && this.props.onSkipped();
    }
  }

  checkProgress() {
    const video = this.videoEl.current;
    if (
      !this.watchedOrSkippedTriggered &&
      video &&
      video.duration &&
      video.currentTime >= video.duration * this.props.ratioToWatch
    ) {
      this.watchedOrSkippedTriggered = true;
      this.props.onWatched && this.props.onWatched();
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  handleClickPoster() {
    this.setState({
      load: true
    });
  }

  triggerAutoPlay() {
    if (this.state.playing || this.autoPlayTriggered) {
      return;
    }
    this.autoPlayTriggered = true;
    const video = this.videoEl.current;
    video.muted = this.storedDefaultMuted;
    attemptAutoPlay(video)
      .catch(error => {
        if (!video.muted) {
          console.info('Unmuted autoplay attempt failed: ' + error);
          // Try playing while muted
          video.muted = true;
          return attemptAutoPlay(video);
        } else {
          throw error;
        }
      })
      .catch(error => {
        console.info('Muted autoplay attempt failed: ' + error);
        // Restore muted setting to default
        video.muted = this.storedDefaultMuted;
        throw error;
      })
      .then(() => {
        // Autoplay succeeded in some form
        this.setState({
          playing: true,
          muted: video.muted,
          restartable: video.muted
        });
      });
  }

  handlePlayChanged(playing) {
    if (this.state.playing !== playing) {
      this.setState({ playing });
      this.checkProgress();
    }
  }

  handleUnmuteButton() {
    const video = this.videoEl.current;
    try {
      video.muted = false;
      video.currentTime = 0;
      if (video.paused || video.ended) {
        video.play();
      }
    } catch (error) {
      // Do nothing
    }
    this.setState({
      playing: true,
      muted: this.videoEl.current.muted,
      restartable: false
    });
  }

  handleVolumeChange() {
    if (this.videoEl.current.muted !== this.state.muted) {
      this.setState({
        muted: this.videoEl.current.muted
      });
    }
  }

  handleError(e) {
    const error = this.videoEl.current.error;
    if (
      error &&
      error instanceof MediaError &&
      error.code === MEDIA_ERR_SRC_NOT_SUPPORTED
    ) {
      this.setState({
        showFallback: true
      });
    }
  }

  render() {
    const {
      defaultAutoPlay,
      children,
      fallback,
      defaultMuted,
      className,
      poster,
      onSkipped,
      onWatched,
      ratioToWatch,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      ...otherProps
    } = this.props;

    const { load, playing, muted, restartable, showFallback } = this.state;

    if (showFallback && fallback) {
      return fallback;
    }

    const setClassName = `${className || ''}${playing ? ' playing' : ''}${
      muted ? ' muted' : ''
    }`;

    return (
      <div
        className={`auto-playing-video ${setClassName}`}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
      >
        {load ? (
          <video
            preload="auto"
            playsInline
            defaultmuted={this.storedDefaultMuted.toString()}
            ref={this.videoEl}
            poster={poster}
            {...otherProps}
            onCanPlay={this.triggerAutoPlay}
            onPlay={this.handlePlay}
            onPause={this.handlePause}
            onEnded={this.handleEnded}
            onVolumeChange={this.handleVolumeChange}
            onError={this.handleError}
          >
            {/* Pass video sources as children */}
            {children}
          </video>
        ) : (
          <TextButton
            onClick={this.handleClickPoster}
            aria-label="Play video"
            className="poster-button"
          >
            <img src={poster} alt="" />
            <A11yFontIcon icon="youtube-play" className="play-icon" alt="" />
          </TextButton>
        )}
        {load && muted && restartable && (
          <TextButton
            onClick={this.handleUnmuteButton}
            className="unmute-button"
          >
            <A11yFontIcon icon="volume-up" large fixed alt="" />
            Press to unmute and restart
          </TextButton>
        )}
      </div>
    );
  }
}

function attemptAutoPlay(videoEl, timeout = PLAY_TIMEOUT) {
  // Based on https://github.com/video-dev/can-autoplay/blob/master/lib/index.js
  return new Promise((resolve, reject) => {
    let timeoutId;
    const sendResolve = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    const sendReject = (error = null) => {
      clearTimeout(timeoutId);
      reject(error);
    };

    const playPromise = videoEl.play();
    timeoutId = setTimeout(() => {
      sendReject(new Error(`Timeout ${timeout} ms has been reached`));
    }, timeout);

    if (playPromise) {
      playPromise
        .then(() => sendResolve())
        .catch(playError => sendReject(playError));
    } else {
      sendResolve();
    }
  });
}

AutoPlayingVideo.defaultProps = {
  defaultAutoPlay: true,
  defaultMuted: false,
  fallback: null,
  ratioToWatch: 1.0
};

AutoPlayingVideo.propTypes = {
  children: PropTypes.node.isRequired, // pass video sources as children
  defaultAutoPlay: PropTypes.bool,
  defaultMuted: PropTypes.bool,
  poster: PropTypes.string,
  fallback: PropTypes.node,
  className: PropTypes.string,
  onSkipped: PropTypes.func,
  onWatched: PropTypes.func,
  ratioToWatch: PropTypes.number
};

export default AutoPlayingVideo;
